/**
 * Central registry of gated downloadable resources.
 *
 * The browser only ever sends a stable `resourceId`. The mapping from that ID to a
 * real file on disk lives here, on the server, so a caller can never influence the
 * filesystem path. This removes the previous design where the client sent a
 * filename (`/api/download?file=...`) that the server then had to sanitise.
 *
 * NOTE ON FILE LOCATIONS: these PDFs currently live under `public/`, so they are
 * ALSO reachable by direct URL. The modal is therefore a lead-capture gate, not an
 * access-control boundary. See README "Download flow — security level" for the
 * honest description and the steps to make gating enforceable.
 */

export type DownloadResource = {
  /** Stable, URL-safe public identifier. Never a filename. */
  id: string;
  /** Human-readable title, shown in the modal. */
  title: string;
  /** Physical file name inside `public/assets/documents/`. */
  fileName: string;
  /** Name the file is saved as by the browser. */
  downloadFilename: string;
  mimeType: string;
  category: 'case-study' | 'datasheet' | 'catalog';
  /** Whether the resource requires the lead-capture form. */
  gated: boolean;
};

/**
 * Resource IDs are validated against this exact shape before any lookup.
 * Anything else is rejected with 400 before touching the filesystem.
 */
export const RESOURCE_ID_PATTERN = /^[a-z0-9][a-z0-9-]{0,63}$/;

/**
 * Which physical file each button serves is PRESERVED from the previous
 * implementation. Note that the catalog and all four product datasheets
 * intentionally resolve to the same source PDF today — that is existing content
 * configuration, not a regression introduced here.
 */
export const DOWNLOAD_RESOURCES: readonly DownloadResource[] = [
  {
    id: 'graphene-case-study',
    title: 'Graphene Performance Case Study',
    fileName: 'Data-sheet-2.pdf',
    downloadFilename: 'Micratto-Graphene-Case-Study.pdf',
    mimeType: 'application/pdf',
    category: 'case-study',
    gated: true,
  },
  {
    id: 'product-catalog',
    title: 'Micratto Product Catalog',
    fileName: 'Data-sheet-1.pdf',
    downloadFilename: 'Micratto-Product-Catalog.pdf',
    mimeType: 'application/pdf',
    category: 'catalog',
    gated: true,
  },
  {
    id: 'tds-graphlube',
    title: 'GraphLube Technical Data Sheet',
    fileName: 'Data-sheet-1.pdf',
    downloadFilename: 'Micratto-GraphLube-TDS.pdf',
    mimeType: 'application/pdf',
    category: 'datasheet',
    gated: true,
  },
  {
    id: 'tds-graphcrete',
    title: 'GraphCrete Technical Data Sheet',
    fileName: 'Data-sheet-1.pdf',
    downloadFilename: 'Micratto-GraphCrete-TDS.pdf',
    mimeType: 'application/pdf',
    category: 'datasheet',
    gated: true,
  },
  {
    id: 'tds-graphcomp',
    title: 'GraphComp Technical Data Sheet',
    fileName: 'Data-sheet-1.pdf',
    downloadFilename: 'Micratto-GraphComp-TDS.pdf',
    mimeType: 'application/pdf',
    category: 'datasheet',
    gated: true,
  },
  {
    id: 'tds-graphflex',
    title: 'GraphFlex Technical Data Sheet',
    fileName: 'Data-sheet-1.pdf',
    downloadFilename: 'Micratto-GraphFlex-TDS.pdf',
    mimeType: 'application/pdf',
    category: 'datasheet',
    gated: true,
  },
];

const RESOURCE_BY_ID = new Map(DOWNLOAD_RESOURCES.map((r) => [r.id, r]));

export function isValidResourceId(id: unknown): id is string {
  return typeof id === 'string' && RESOURCE_ID_PATTERN.test(id);
}

/** Returns the resource, or `null` for unknown/malformed IDs. Never throws. */
export function getDownloadResource(id: unknown): DownloadResource | null {
  if (!isValidResourceId(id)) return null;
  return RESOURCE_BY_ID.get(id) ?? null;
}
